/**
 * Admin › Groups — DESIGN.md §9.3, §6.9.
 *
 * Groups exist so `@Support` in a comment notifies the whole team (§6.9) and
 * so an automation can add one as collaborators (§14.2). They grant no
 * permissions of their own.
 */
import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { Plus, Trash2, UsersRound } from 'lucide-react';
import { toast } from 'sonner';
import { ApiError } from '@/lib/api';
import {
  useCreateGroup,
  useDeleteGroup,
  useGroup,
  useGroups,
  useSetGroupMembers,
  useUsers,
} from '@/features/admin/users-api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { UserAvatar } from '@/components/ui/avatar';
import { EmptyState } from '@/components/ui/empty-state';
import { LoadingRegion, Spinner } from '@/components/ui/spinner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/admin/groups')({
  component: AdminGroupsPage,
});

function AdminGroupsPage(): JSX.Element {
  const groups = useGroups();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  const activeId = selectedId ?? groups.data?.[0]?.id ?? null;

  // Owned by the page, not by `GroupDetail`: deleting a group unmounts the
  // detail pane, and React Query does not run `mutate()`-level callbacks for
  // an observer that has gone away — the success toast would never appear.
  const deleteGroup = useDeleteGroup();

  const handleDelete = (group: { id: string; name: string; memberCount: number }): void => {
    if (
      !window.confirm(
        `Delete the group "${group.name}"? Its ${group.memberCount} members keep their accounts.`,
      )
    ) {
      return;
    }
    deleteGroup.mutate(group.id, {
      onSuccess: () => {
        toast.success(`Deleted ${group.name}`);
        setSelectedId(null);
      },
      onError: (error) =>
        toast.error(error instanceof ApiError ? error.message : 'Could not delete'),
    });
  };

  if (groups.isLoading) return <LoadingRegion label="Loading groups" />;

  if (!groups.data?.length) {
    return (
      <>
        <EmptyState
          icon={<UsersRound className="size-8" />}
          title="No groups yet"
          description="Groups let one @mention reach a whole team, and let automations add a team as collaborators. Development, Support, and Management are the usual three."
          action={
            <Button onClick={() => setCreateOpen(true)}>
              <Plus />
              New group
            </Button>
          }
        />
        <CreateGroupDialog open={createOpen} onOpenChange={setCreateOpen} />
      </>
    );
  }

  return (
    <div className="grid gap-4 md:grid-cols-[16rem_1fr]">
      <div className="space-y-2">
        <ul className="bg-card space-y-1 rounded-lg border p-1">
          {groups.data.map((group) => (
            <li key={group.id}>
              <button
                type="button"
                onClick={() => setSelectedId(group.id)}
                className={cn(
                  'flex w-full items-center justify-between gap-2 rounded-md px-3 py-2 text-left text-sm transition-colors',
                  group.id === activeId ? 'bg-accent font-medium' : 'hover:bg-accent/60',
                )}
              >
                <span className="truncate">{group.name}</span>
                <span className="text-muted-foreground shrink-0 text-xs">{group.memberCount}</span>
              </button>
            </li>
          ))}
        </ul>
        <Button variant="outline" className="w-full" onClick={() => setCreateOpen(true)}>
          <Plus />
          New group
        </Button>
      </div>

      {activeId ? (
        <GroupDetail
          groupId={activeId}
          onDelete={handleDelete}
          isDeleting={deleteGroup.isPending}
        />
      ) : null}
      <CreateGroupDialog open={createOpen} onOpenChange={setCreateOpen} />
    </div>
  );
}

function GroupDetail({
  groupId,
  onDelete,
  isDeleting,
}: {
  groupId: string;
  onDelete: (group: { id: string; name: string; memberCount: number }) => void;
  isDeleting: boolean;
}): JSX.Element {
  const group = useGroup(groupId);
  const users = useUsers({ active: 'true', q: '' });
  const setMembers = useSetGroupMembers();

  if (group.isLoading || !group.data) return <LoadingRegion label="Loading group" />;

  const memberIds = new Set(group.data.members.map((member) => member.id));

  const toggle = (userId: string, next: boolean): void => {
    const updated = next ? [...memberIds, userId] : [...memberIds].filter((id) => id !== userId);
    setMembers.mutate(
      { id: groupId, memberIds: updated },
      {
        onError: (error) =>
          toast.error(error instanceof ApiError ? error.message : 'Could not update membership'),
      },
    );
  };

  return (
    <div className="bg-card rounded-lg border p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold">{group.data.name}</h2>
          {group.data.description ? (
            <p className="text-muted-foreground mt-1 text-sm">{group.data.description}</p>
          ) : null}
          <p className="text-muted-foreground mt-1 text-xs">
            Mention this group on a card as{' '}
            <code className="bg-muted rounded px-1 py-0.5">@{group.data.name}</code> to notify
            everyone in it.
          </p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="text-destructive hover:bg-destructive/10 hover:text-destructive"
          disabled={isDeleting}
          onClick={() =>
            onDelete({
              id: groupId,
              name: group.data.name,
              memberCount: group.data.memberCount,
            })
          }
        >
          <Trash2 />
          Delete
        </Button>
      </div>

      <div className="mt-5">
        <h3 className="mb-2 flex items-center gap-2 text-sm font-medium">
          Members
          {setMembers.isPending ? <Spinner /> : null}
        </h3>
        {users.data?.items.length ? (
          <ul className="divide-y rounded-md border">
            {users.data.items.map((user) => (
              <li key={user.id} className="flex items-center gap-3 px-3 py-2">
                <Checkbox
                  id={`member-${user.id}`}
                  checked={memberIds.has(user.id)}
                  disabled={setMembers.isPending}
                  onCheckedChange={(checked) => toggle(user.id, checked === true)}
                />
                <UserAvatar user={user} />
                <Label htmlFor={`member-${user.id}`} className="min-w-0 flex-1 cursor-pointer">
                  <span className="block truncate text-sm font-normal">{user.name}</span>
                  <span className="text-muted-foreground block truncate text-xs">{user.email}</span>
                </Label>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-muted-foreground text-sm">No active users to add yet.</p>
        )}
      </div>
    </div>
  );
}

function CreateGroupDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}): JSX.Element {
  const createGroup = useCreateGroup();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New group</DialogTitle>
          <DialogDescription>
            A group is a mention target and an automation audience — not a permission boundary.
          </DialogDescription>
        </DialogHeader>
        <form
          id="create-group"
          className="grid gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            createGroup.mutate(
              { name, description: description || null },
              {
                onSuccess: (group) => {
                  toast.success(`Created ${group.name}`);
                  setName('');
                  setDescription('');
                  onOpenChange(false);
                },
                onError: (error) =>
                  toast.error(error instanceof ApiError ? error.message : 'Could not create'),
              },
            );
          }}
        >
          <div className="space-y-1.5">
            <Label htmlFor="group-name">Name</Label>
            <Input
              id="group-name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              required
              autoFocus
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="group-description">Description</Label>
            <Input
              id="group-description"
              value={description}
              onChange={(event) => setDescription(event.target.value)}
            />
          </div>
        </form>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="submit" form="create-group" disabled={createGroup.isPending || !name}>
            {createGroup.isPending ? <Spinner /> : null}
            Create group
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
